/**
 * Transaction Review Panel
 *
 * Shows transactions flagged for review due to potential duplicates
 * with mismatched data (amount or date discrepancies).
 */

import React from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Alert,
  Popconfirm,
  message,
  Empty,
  Tooltip,
} from 'antd';
import {
  WarningOutlined,
  CheckOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bankTransactionsApi } from '../../services/api';
import { colors } from '../design-system/tokens';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

interface TransactionReviewPanelProps {
  onResolve?: () => void;
}

const TransactionReviewPanel: React.FC<TransactionReviewPanelProps> = ({
  onResolve,
}) => {
  const queryClient = useQueryClient();

  // Fetch transactions needing review
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bank-transactions', 'needs-review'],
    queryFn: () => bankTransactionsApi.getNeedsReview(20),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Handle both AxiosResponse wrapper and direct data
  const rawData = (data as any)?.data || data;
  const transactions = rawData?.transactions || [];

  // Mutation to resolve a review
  const resolveMutation = useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: 'keep' | 'delete' }) =>
      bankTransactionsApi.resolveReview(id, resolution),
    onSuccess: (_, { resolution }) => {
      message.success(
        resolution === 'keep'
          ? 'Transaction kept - review cleared'
          : 'Transaction deleted'
      );
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      refetch();
      onResolve?.();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to resolve review');
    },
  });

  const handleResolve = (id: string, resolution: 'keep' | 'delete') => {
    resolveMutation.mutate({ id, resolution });
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 150,
      render: (ref: string) => <Text code>{ref}</Text>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right' as const,
      render: (amount: number) => (
        <Text strong>${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Text>
      ),
    },
    {
      title: 'Issue',
      dataIndex: 'reviewReason',
      key: 'reviewReason',
      render: (reason: string) => (
        <Tooltip title={reason}>
          <Paragraph
            ellipsis={{ rows: 2 }}
            style={{ margin: 0, color: colors.status.warning }}
          >
            <WarningOutlined style={{ marginRight: 4 }} />
            {reason}
          </Paragraph>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="Keep this transaction (clear the review flag)">
            <Popconfirm
              title="Keep this transaction?"
              description="This will clear the review flag and keep the transaction as entered."
              onConfirm={() => handleResolve(record.id, 'keep')}
              okText="Keep"
              cancelText="Cancel"
            >
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                loading={resolveMutation.isPending}
              >
                Keep
              </Button>
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Delete this duplicate transaction">
            <Popconfirm
              title="Delete this transaction?"
              description="This transaction will be permanently deleted."
              onConfirm={() => handleResolve(record.id, 'delete')}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={resolveMutation.isPending}
              >
                Delete
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Don't render if no transactions need review
  if (!isLoading && transactions.length === 0) {
    return null;
  }

  return (
    <Card
      title={
        <Space>
          <WarningOutlined style={{ color: colors.status.warning }} />
          <span>Transactions Needing Review</span>
          <Tag color="orange">{transactions.length}</Tag>
        </Space>
      }
      extra={
        <Tooltip title="These transactions have matching reference numbers but different amounts or dates. Review and resolve each one.">
          <InfoCircleOutlined style={{ color: colors.text.secondary }} />
        </Tooltip>
      }
      style={{ marginBottom: 16 }}
    >
      <Alert
        type="warning"
        message="Potential Duplicate Transactions"
        description="These transactions were flagged because an import found matching reference numbers but the amount or date didn't match. Please review and decide whether to keep or delete each one."
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Table
        columns={columns}
        dataSource={transactions}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        size="small"
        locale={{
          emptyText: <Empty description="No transactions need review" />,
        }}
      />
    </Card>
  );
};

export default TransactionReviewPanel;
